import { TransitionGroup } from 'react-transition-group';
import React, {
    useCallback,
    useEffect,
    useState,
    ReactElement,
    ReactNode,
} from 'react';

import LocalCall from './LocalCall';
import RemoteCall from './RemoteCall';
import Meeting from '../meeting/Meeting';
import { CallTransition, MeetingTransition } from './CSSTransition';
import { RTCSession } from 'jssip/lib/RTCSession';
import FocusedLocalCallContext from 'flow/contexts/call-item-size-context';
import {
    getFocusedLocalCallId,
    getLocalCallSessions,
} from 'flow/selectors/call-selectors';
import { useAppSelector } from 'flow/hooks/useRedux';
import { TypedMap } from 'flow/types/TypedMap';
import { ActiveLineData } from 'flow/types/ActiveLineData';

interface Props {
    remoteCall: TypedMap<ActiveLineData>;
    hasActiveMeeting: boolean;
}

const CallList = ({ remoteCall, hasActiveMeeting }: Props) => {
    const localSessions = useAppSelector(getLocalCallSessions);

    const localCallIds = localSessions
        .map((session: RTCSession) => session.data['callId'])
        .valueSeq()
        .toArray();

    const [visibleLocalCallNodes, setVisibleLocalCallNodes] = useState(
        new Map(),
    );
    const [render, setRender] = useState(false);

    const focusedLocalCallId = useAppSelector(getFocusedLocalCallId);

    const meetingCode = useAppSelector((state) =>
        state.meeting.getIn(['activeMeeting', 'meetingCode']),
    );

    // This variable is needed because as soon as a call is ended it is removed from the store,
    // but it is still visible during its exit transition. This variable enabled us to let a
    // large call item that is being transitioned out to stay large during that period
    const [renderedFocusedLocalCallId, setRenderedFocusedLocalCallId] =
        useState(undefined);

    const [spawnedNotifications, setSpawnedNotifications] = useState([]);
    const onNotificationSpawned = (notificationDedupKey: string) => {
        setSpawnedNotifications((spawnedNotification) => [
            ...spawnedNotification,
            notificationDedupKey,
        ]);
    };

    // Hack: For some reason the enter transition doesn't work unless we let the TransitionGroup mount before we render the rest
    useEffect(() => {
        const timer = setTimeout(() => setRender(true));
        return () => {
            clearTimeout(timer);
        };
    }, []);

    useEffect(() => {
        const renderedItemIsExiting = !localCallIds?.includes(
            renderedFocusedLocalCallId,
        );
        const focusedCallIsMeeting = renderedFocusedLocalCallId === meetingCode;
        if (
            renderedFocusedLocalCallId &&
            renderedItemIsExiting &&
            !focusedCallIsMeeting
        ) {
            // Let the large call item stay large while exiting
            return;
        }

        setRenderedFocusedLocalCallId(focusedLocalCallId);
    }, [
        focusedLocalCallId,
        renderedFocusedLocalCallId,
        localCallIds,
        meetingCode,
    ]);

    const onEndExitMeeting = useCallback(
        (meetingCode: string) => () => {
            //When an meeting is hung up, it should not stay rendered in a transition like a call does
            if (renderedFocusedLocalCallId === meetingCode) {
                setRenderedFocusedLocalCallId(undefined);
            }
        },
        [renderedFocusedLocalCallId],
    );

    const onEnterStarted = useCallback(
        (callId: string) => (node: ReactNode) => {
            setVisibleLocalCallNodes(
                new Map(visibleLocalCallNodes.set(callId, node)),
            );
        },
        [visibleLocalCallNodes],
    );

    const onExitEnded = useCallback(
        (callId: string) => () => {
            visibleLocalCallNodes.delete(callId);
            setVisibleLocalCallNodes(new Map(visibleLocalCallNodes));

            if (callId === renderedFocusedLocalCallId) {
                // If the current rendered large call has exited, we want a new call item to be large.
                // A gotcha here is that since the item is removed from the components children at the
                // start of the exit transition, the prop 'onExited' has stopped updating and we don't know
                // the current 'largeCallItemCallId', which is why we set it to undefined and let the
                // component choose a new one itself.
                setRenderedFocusedLocalCallId(undefined);
            }
        },
        [visibleLocalCallNodes, renderedFocusedLocalCallId],
    );

    const childFactoryFunction =
        (hasLocalCall: boolean) => (child: ReactElement) => {
            if (child.props.isRemote) {
                const otherRemoteCall =
                    remoteCall?.get('key') !== child.props.callKey;
                if (hasLocalCall || otherRemoteCall) {
                    // Disable enter and exit animations if a local call or another remote call is available, making it disappear immediately.
                    // If a child has already initiated the exit, there seems to be no clean way to exit immediately. This is why the hidden prop
                    // is forwarded to the child for it to hide itself
                    return React.cloneElement(child, {
                        enter: false,
                        exit: false,
                        hidden: true,
                    });
                }
            }
            return child;
        };

    const hasVisibleLocalCall =
        localCallIds?.length > 0 || visibleLocalCallNodes.size > 0;

    return (
        <FocusedLocalCallContext.Provider value={renderedFocusedLocalCallId}>
            <TransitionGroup
                component={null}
                childFactory={childFactoryFunction(hasVisibleLocalCall)}
            >
                {render && hasActiveMeeting && (
                    <MeetingTransition onExited={onEndExitMeeting(meetingCode)}>
                        <div
                            style={
                                renderedFocusedLocalCallId === meetingCode &&
                                remoteCall
                                    ? { width: '50%' }
                                    : {}
                            }
                        >
                            <Meeting />
                        </div>
                    </MeetingTransition>
                )}
                {render &&
                    localSessions?.map((session: RTCSession) => {
                        const callId = session.data['callId'];
                        return (
                            <CallTransition
                                key={callId}
                                onEnter={onEnterStarted(callId)}
                                onExited={onExitEnded(callId)}
                                isRemote={false}
                            >
                                <LocalCall
                                    session={session}
                                    callId={callId}
                                    spawnedNotifications={spawnedNotifications}
                                    onNotificationSpawned={
                                        onNotificationSpawned
                                    }
                                />
                            </CallTransition>
                        );
                    })}
                {render && remoteCall && !hasVisibleLocalCall && (
                    <CallTransition
                        key={remoteCall.get('key')}
                        callKey={remoteCall.get('key')}
                        isRemote={true}
                    >
                        <RemoteCall
                            activeCall={remoteCall}
                            spawnedNotifications={spawnedNotifications}
                            onNotificationSpawned={onNotificationSpawned}
                        />
                    </CallTransition>
                )}
            </TransitionGroup>
        </FocusedLocalCallContext.Provider>
    );
};

export default React.memo(CallList);
